<template>
	<FormKit
		v-model="vModel"
		type="form"
		:actions="false"
		form-class="relative z-20 mb-10 border-b border-border-secondary max-sm:mx-4"
	>
		<HorizontalScroll
			class="py-3 sm:py-5 w-full flex items-end gap-4 lg:gap-10"
			list-tag="div"
			fade-edges
		>
			<FormKit
				name="countries"
				type="selectbox"
				:value="vModel.countries"
				:options="countriesOptions"
				label="Země původu"
				:placeholder="`Země původu (${isAction ? actionCountriesCount : countriesCount})`"
				:classes="{inner: 'rem:min-w-[240px] mb-0', label: 'mb-2 block typo-body-s text-body-secondary', input: 'py-[.6875rem]'}"
				multiple="true"
				search="true"
			/>
			<FormKit
				name="brands"
				type="selectbox"
				:value="vModel.brands"
				:options="brandsOptions"
				label="Značka"
				:placeholder="`Značka (${isAction ? actionBrandsCount : brandsCount})`"
				:classes="{inner: 'rem:min-w-[240px] mb-0', label: 'mb-2 block typo-body-s text-body-secondary', input: 'py-[.6875rem]'}"
				multiple="true"
				search="true"
			/>
		</HorizontalScroll>
	</FormKit>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import type {FormKitOptionsProp} from '@formkit/inputs';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {
		Gsoa_ProductCatalog_Enums_Country as CountryEnum,
		Gsoa_ProductCatalog_Eshop_Brand as BrandEnum,
	} from '~/openapi';
	import type {ProductCatalogFilter} from '~/types/ProductCatalogFilter';
	import {useProductCatalogStore} from '~/stores/productCatalog';

	interface Props {
		modelValue: ProductCatalogFilter
	}

	interface Emits {
		(e: 'update:modelValue', payload: ProductCatalogFilter): void
	}

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();
	const vModel = useVModel(props, 'modelValue', emit);
	const {name: routeName} = useRoute();

	const productCatalogStore = useProductCatalogStore();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const houseNumber = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);

	const isActionCategory = computed(() => routeName === 'branch-hypermarket-akcni-nabidka-category');
	const isCatalogCategory = computed(() => routeName === 'branch-hypermarket-cela-nabidka-category');

	// Each listing type needs its own key – during client navigation between action offer and full offer the new page
	// is set up while the old one is still mounted, so a shared key would reuse the old page's asyncData and handler
	await useLazyAsyncData(
		`productCatalogCountriesAndBrands-${isActionCategory.value ? 'actionOffer' : 'productCatalog'}-${houseNumber.value ?? 'none'}`,
		async () => {
			if (!houseNumber.value) {
				return null;
			}

			if (isActionCategory.value) {
				await Promise.all([
					productCatalogStore.fetchActionCountries(),
					productCatalogStore.fetchActionBrands(),
				]);
				return true;
			}

			if (isCatalogCategory.value) {
				await Promise.all([
					productCatalogStore.fetchCountries(),
					productCatalogStore.fetchBrands(),
				]);
				return true;
			}

			return null;
		},
		{watch: [houseNumber, isActionCategory, isCatalogCategory]},
	);

	const {countries, brands, actionBrands, actionCountries, countriesCount, actionCountriesCount, brandsCount, actionBrandsCount} = storeToRefs(productCatalogStore);

	const isAction = computed(() => routeName === 'branch-hypermarket-akcni-nabidka-category');

	const countriesOptions = computed(() => {
		const source = isAction.value ? actionCountries.value : countries.value;
		return source?.countries?.map((item: CountryEnum) => ({
			label: item.name,
			value: item.id,
		})) as FormKitOptionsProp;
	});

	const brandsOptions = computed(() => {
		const source = isAction.value ? actionBrands.value : brands.value;
		return source?.brands?.map((item: BrandEnum) => ({
			label: item.name,
			value: item.id,
		})) as FormKitOptionsProp;
	});
</script>
